import React from 'react';

import { TextField } from 'Containers/App/types';
import { ContentfulCustomContentBlock, RichTextContent } from 'ContentfulDefaults/types/_shared';
import { useFeatureFlagContext } from 'Contexts/featureFlagContext';
import { useFederalLimitsContext } from 'Contexts/federalLimitsContext';
import { useTextContext } from 'Contexts/textContext';
import { H5 } from 'DesignLibrary/atoms';
import RichText from 'Shared/RichText';
import Text from 'Shared/Text';
import { EducationalDrawerInfoTypes, SAVINGS_ACCOUNT_TYPES } from 'Types/entities';
import { toDollars } from 'Utils/helpers';
import { makeYearReplacement } from 'Utils/textReplacement';

import { HowContainer } from './styled';

// The HSA "how it works" copy hardcodes the year in Contentful (e.g. "The 2026 limits
// are:"). Must keep supporting this literal, not just a "{year}" placeholder, so Contentful
// can switch to a placeholder later without needing a code change alongside it.
const HSA_COPY_HARDCODED_YEAR = '2026';

interface HowItWorksProps {
  infoType: EducationalDrawerInfoTypes;
  individualHsaContributionLimit: number | undefined;
  familyHsaContributionLimit: number | undefined;
  employerContribution: number | null;
  showLimitedProviderHraEducation?: boolean;
}

export const HowItWorks = ({
  infoType,
  individualHsaContributionLimit,
  familyHsaContributionLimit,
  employerContribution,
  showLimitedProviderHraEducation = false,
}: HowItWorksProps) => {
  const { retrieveContentfulData } = useTextContext();
  const base = `health_section.secondary_content.${infoType}_modal`;

  let alternateHeaderKey: string | undefined;
  if (infoType === SAVINGS_ACCOUNT_TYPES.HRA && showLimitedProviderHraEducation) {
    alternateHeaderKey = 'garner_hra';
  }
  const alternateHeaderPath = alternateHeaderKey
    ? (`${base}.how_does_it_work_alternate_headers.${alternateHeaderKey}` as TextField)
    : undefined;
  const alternateHeaderText = alternateHeaderPath
    ? retrieveContentfulData<string>(alternateHeaderPath)
    : undefined;
  const headerField: TextField = alternateHeaderText
    ? (alternateHeaderPath as TextField)
    : (`${base}.how_does_it_work_header_text` as TextField);

  let vars;
  if (infoType === SAVINGS_ACCOUNT_TYPES.HSA) {
    vars = [toDollars(individualHsaContributionLimit), toDollars(familyHsaContributionLimit)];
  } else if (infoType === SAVINGS_ACCOUNT_TYPES.HRA) {
    vars = [toDollars(employerContribution)];
  }

  const { is_federal_limits_from_jvp_enabled: isFederalLimitsEnabled } = useFeatureFlagContext();
  const selectedPlanYear = useFederalLimitsContext()?.year;
  const hsaYearReplacement =
    isFederalLimitsEnabled && infoType === SAVINGS_ACCOUNT_TYPES.HSA
      ? makeYearReplacement(selectedPlanYear, HSA_COPY_HARDCODED_YEAR)
      : undefined;

  const blocks =
    retrieveContentfulData<ContentfulCustomContentBlock[]>(
      `${base}.how_does_it_work_content_blocks` as TextField,
    ) ?? [];

  const visibleBlocks = blocks.filter((block) => {
    if (block.block_id === 'hra_modal_how_it_works_standard_hra' && showLimitedProviderHraEducation) {
      return false;
    }
    if (block.block_id === 'hra_modal_how_it_works_garner_hra' && !showLimitedProviderHraEducation) {
      return false;
    }
    return true;
  });

  const mergedListItems: RichTextContent[] = [];
  visibleBlocks.forEach((block) => {
    const ul = block.content?.content?.find((node) => node.nodeType === 'unordered-list');
    if (ul?.content) mergedListItems.push(...ul.content);
  });

  let body: React.ReactNode;
  if (mergedListItems.length > 0) {
    const mergedDoc: RichTextContent = {
      data: {},
      nodeType: 'document',
      content: [{ data: {}, nodeType: 'unordered-list', content: mergedListItems }],
    };
    body = (
      <RichText
        field={undefined}
        fieldContent={mergedDoc}
        vars={vars}
        textReplacements={hsaYearReplacement}
        inlineParagraphs
      />
    );
  } else {
    body = (
      <RichText
        field={`${base}.how_does_it_work_content` as TextField}
        vars={vars}
        textReplacements={hsaYearReplacement}
        inlineParagraphs
      />
    );
  }

  return (
    <HowContainer>
      <H5 as="h2">
        <Text field={headerField} />
      </H5>
      {body}
    </HowContainer>
  );
};
